import React, { useMemo } from 'react';
import { useTheme } from '../../../theme';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform,
  Animated,
  Linking,
  Dimensions,
} from 'react-native';
import { useSelector } from 'react-redux';
import Svg, { G, Circle, Line, Path } from 'react-native-svg';
import DLOTooltip from './DLOTooltip';
import LHSidebarItems from '../../../components/LHSidebarItems';
import type { RootState } from '../../../store';



interface MobileSidepanelProps {
  isVisible: boolean;
  onClose: () => void;
  activeScreen: string;
  onNavigate: (screen: string) => void;
}

// Helper function to get tooltip content based on sidebar item ID
const getTooltipContent = (itemId: string): { title: string; description: string } => {
  const idMapping: { [key: string]: string } = {
    'home': 'Home',
    'observations': 'Observations',
    'shift-handover': 'Shift Handover',
    'leadership-walk': 'Leadership Walk',
    'sparc-walk': 'SPARC Walk',
    'nho': 'NHO',
    'racking-inspection': 'Racking Inspection',
    'quality-audit': 'Quality Audit',
    'site-evaluation': 'Site Evaluation',
    
    'labor-optimization': 'DLO',
    'contact-feedback': 'Contact us/Feedback',
  };

  const mappedLabel = idMapping[itemId];
  const sidebarItem = LHSidebarItems.find(item => item.label === mappedLabel);
  
  if (sidebarItem) {
    return {
      title: sidebarItem.label,
      description: sidebarItem.description || `Navigate to ${sidebarItem.label}`
    };
  }
  
  return {
    title: itemId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    description: `Navigate to ${itemId.replace('-', ' ')}`
  };
};

// SVG Icons
const CloseIcon: React.FC<{ color?: string }> = ({ color = '#EFEFEF' }) => (
  <Path
    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
    fill="#EFEFEF"
  />
);

const HomeIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M10 4.74167L14.1667 8.49167V15H12.5V10H7.50002V15H5.83335V8.49167L10 4.74167V4.74167ZM10 2.5L1.66669 10H4.16669V16.6667H9.16669V11.6667H10.8334V16.6667H15.8334V10H18.3334L10 2.5Z"
    fill="#3A3A3A"
  />
);

const VisibilityIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M9.99998 5C13.1583 5 15.975 6.775 17.35 9.58333C15.975 12.3917 13.1583 14.1667 9.99998 14.1667C6.84165 14.1667 4.02498 12.3917 2.64998 9.58333C4.02498 6.775 6.84165 5 9.99998 5ZM9.99998 3.33333C5.83331 3.33333 2.27498 5.925 0.833313 9.58333C2.27498 13.2417 5.83331 15.8333 9.99998 15.8333C14.1666 15.8333 17.725 13.2417 19.1666 9.58333C17.725 5.925 14.1666 3.33333 9.99998 3.33333ZM9.99998 7.5C11.15 7.5 12.0833 8.43333 12.0833 9.58333C12.0833 10.7333 11.15 11.6667 9.99998 11.6667C8.84998 11.6667 7.91665 10.7333 7.91665 9.58333C7.91665 8.43333 8.84998 7.5 9.99998 7.5ZM9.99998 5.83333C7.93331 5.83333 6.24998 7.51667 6.24998 9.58333C6.24998 11.65 7.93331 13.3333 9.99998 13.3333C12.0666 13.3333 13.75 11.65 13.75 9.58333C13.75 7.51667 12.0666 5.83333 9.99998 5.83333Z"
    fill="#3A3A3A"
  />
);

const SwapIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M5.825 9.16666L2.5 12.5L5.825 15.8333V13.3333H11.6667V11.6667H5.825V9.16666ZM17.5 7.5L14.175 4.16666V6.66666H8.33333V8.33333H14.175V10.8333L17.5 7.5Z"
    fill="#3A3A3A"
  />
);

const SupervisorIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M7.50002 10C9.10835 10 10.4167 8.69166 10.4167 7.08333C10.4167 5.475 9.10835 4.16666 7.50002 4.16666C5.89169 4.16666 4.58335 5.475 4.58335 7.08333C4.58335 8.69166 5.89169 10 7.50002 10ZM7.50002 5.83333C8.19169 5.83333 8.75002 6.39166 8.75002 7.08333C8.75002 7.775 8.19169 8.33333 7.50002 8.33333C6.80835 8.33333 6.25002 7.775 6.25002 7.08333C6.25002 6.39166 6.80835 5.83333 7.50002 5.83333ZM7.54169 14.1667H3.97502C4.80002 13.75 6.22502 13.3333 7.50002 13.3333C7.59169 13.3333 7.69169 13.3417 7.78335 13.3417C8.06669 12.7333 8.55835 12.2333 9.15002 11.8333C8.54169 11.725 7.96669 11.6667 7.50002 11.6667C5.55002 11.6667 1.66669 12.6417 1.66669 14.5833V15.8333H7.50002V14.5833C7.50002 14.4417 7.51669 14.3 7.54169 14.1667ZM13.75 12.0833C12.2167 12.0833 9.16669 12.925 9.16669 14.5833V15.8333H18.3334V14.5833C18.3334 12.925 15.2834 12.0833 13.75 12.0833ZM14.7584 10.5667C15.3917 10.2083 15.8334 9.53333 15.8334 8.75C15.8334 7.6 14.9 6.66666 13.75 6.66666C12.6 6.66666 11.6667 7.6 11.6667 8.75C11.6667 9.53333 12.1084 10.2083 12.7417 10.5667C13.0417 10.7333 13.3834 10.8333 13.75 10.8333C14.1167 10.8333 14.4584 10.7333 14.7584 10.5667Z"
    fill="#3A3A3A"
  />
);

const WalkIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M11.25 4.58333C12.1667 4.58333 12.9167 3.83333 12.9167 2.91667C12.9167 2 12.1667 1.25 11.25 1.25C10.3333 1.25 9.58333 2 9.58333 2.91667C9.58333 3.83333 10.3333 4.58333 11.25 4.58333ZM8.16667 7.41667L5.83333 19.1667H7.58333L9.08333 12.5L10.8333 14.1667V19.1667H12.5V12.9167L10.75 11.25L11.25 8.75C12.3333 10 14 10.8333 15.8333 10.8333V9.16667C14.25 9.16667 12.9167 8.33333 12.25 7.16667L11.4167 5.83333C10.95 5.09167 10.0167 4.79167 9.20833 5.13333L5 6.91667V10.8333H6.66667V8L8.16667 7.41667Z"
    fill="#3A3A3A"
  />
);

const ExploreIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M10 1.66667C5.40002 1.66667 1.66669 5.40001 1.66669 10C1.66669 14.6 5.40002 18.3333 10 18.3333C14.6 18.3333 18.3334 14.6 18.3334 10C18.3334 5.40001 14.6 1.66667 10 1.66667ZM10 16.6667C6.32502 16.6667 3.33335 13.675 3.33335 10C3.33335 6.32501 6.32502 3.33334 10 3.33334C13.675 3.33334 16.6667 6.32501 16.6667 10C16.6667 13.675 13.675 16.6667 10 16.6667ZM5.41669 14.5833L11.675 11.675L14.5834 5.41667L8.32502 8.32501L5.41669 14.5833ZM10 9.08334C10.5084 9.08334 10.9167 9.49167 10.9167 10C10.9167 10.5083 10.5084 10.9167 10 10.9167C9.49169 10.9167 9.08335 10.5083 9.08335 10C9.08335 9.49167 9.49169 9.08334 10 9.08334Z"
    fill="#3A3A3A"
  />
);

const PersonAddIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M10.8333 6.66666C10.8333 4.82499 9.34165 3.33333 7.49998 3.33333C5.65831 3.33333 4.16665 4.82499 4.16665 6.66666C4.16665 8.50833 5.65831 9.99999 7.49998 9.99999C9.34165 9.99999 10.8333 8.50833 10.8333 6.66666ZM9.16665 6.66666C9.16665 7.58333 8.41665 8.33333 7.49998 8.33333C6.58331 8.33333 5.83331 7.58333 5.83331 6.66666C5.83331 5.74999 6.58331 4.99999 7.49998 4.99999C8.41665 4.99999 9.16665 5.74999 9.16665 6.66666ZM0.833313 15V16.6667H14.1666V15C14.1666 12.7833 9.72498 11.6667 7.49998 11.6667C5.27498 11.6667 0.833313 12.7833 0.833313 15ZM2.49998 15C2.66665 14.4083 5.24998 13.3333 7.49998 13.3333C9.74165 13.3333 12.3166 14.4 12.5 15H2.49998ZM16.6666 12.5V9.99999H19.1666V8.33333H16.6666V5.83333H15V8.33333H12.5V9.99999H15V12.5H16.6666Z"
    fill="#3A3A3A"
  />
);

const InventoryIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M2.25 4H4.25V7H14.25V4H16.25V9H18.25V4C18.25 2.9 17.35 2 16.25 2H12.07C11.65 0.84 10.55 0 9.25 0C7.95 0 6.85 0.84 6.43 2H2.25C1.15 2 0.25 2.9 0.25 4V18C0.25 19.1 1.15 20 2.25 20H8.25V18H2.25V4ZM9.25 2C9.8 2 10.25 2.45 10.25 3C10.25 3.55 9.8 4 9.25 4C8.7 4 8.25 3.55 8.25 3C8.25 2.45 8.7 2 9.25 2Z M18.25 10.5L12.76 16L9.75 13L8.25 14.5L12.76 19L19.75 12L18.25 10.5Z"
    fill="#3A3A3A"
  />
);

const PasteIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M15.8333 1.66667H12.35C12 0.7 11.0833 0 10 0C8.91667 0 8 0.7 7.65 1.66667H4.16667C3.25 1.66667 2.5 2.41667 2.5 3.33333V16.6667C2.5 17.5833 3.25 18.3333 4.16667 18.3333H15.8333C16.75 18.3333 17.5 17.5833 17.5 16.6667V3.33333C17.5 2.41667 16.75 1.66667 15.8333 1.66667ZM10 1.66667C10.4583 1.66667 10.8333 2.04167 10.8333 2.5C10.8333 2.95833 10.4583 3.33333 10 3.33333C9.54167 3.33333 9.16667 2.95833 9.16667 2.5C9.16667 2.04167 9.54167 1.66667 10 1.66667ZM15.8333 16.6667H4.16667V3.33333H5.83333V5.83333H14.1667V3.33333H15.8333V16.6667Z"
    fill="#3A3A3A"
  />
);

const VerificationIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M13.6 6.88L12.18 5.46L7.94 9.71L5.82 7.58L4.4 9L7.94 12.54L13.6 6.88Z M16 0H2C0.89 0 0 0.9 0 2V14C0 15.1 0.89 16 2 16H16C17.1 16 18 15.1 18 14V2C18 0.9 17.11 0 16 0ZM16 14H2V4H16V14Z"
    fill="#3A3A3A"
  />
);

const AnalyticsIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M3 17H5V11H3V17ZM7 17H9V7H7V17ZM11 17H13V13H11V17ZM15 17H17V4H15V17ZM2 19H18V21H2V19Z"
    fill="#3A3A3A"
  />
);

const TrendingIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M13.3334 5L15.2417 6.90833L11.175 10.975L7.84169 7.64167L1.66669 13.825L2.84169 15L7.84169 10L11.175 13.3333L16.425 8.09167L18.3334 10V5H13.3334Z"
    fill="#3A3A3A"
  />
);

const MailIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M18.3334 4.99998C18.3334 4.08331 17.5834 3.33331 16.6667 3.33331H3.33335C2.41669 3.33331 1.66669 4.08331 1.66669 4.99998V15C1.66669 15.9166 2.41669 16.6666 3.33335 16.6666H16.6667C17.5834 16.6666 18.3334 15.9166 18.3334 15V4.99998ZM16.6667 4.99998L10 9.15831L3.33335 4.99998H16.6667ZM16.6667 15H3.33335V6.66665L10 10.8333L16.6667 6.66665V15Z"
    fill="#3A3A3A"
  />
);

const FolderIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M16.6667 5.00001H10L8.33335 3.33334H3.33335C2.41669 3.33334 1.67502 4.08334 1.67502 5.00001L1.66669 15C1.66669 15.9167 2.41669 16.6667 3.33335 16.6667H16.6667C17.5834 16.6667 18.3334 15.9167 18.3334 15V6.66668C18.3334 5.75001 17.5834 5.00001 16.6667 5.00001ZM16.6667 15H3.33335V6.66668H16.6667V15Z"
    fill="#3A3A3A"
  />
);

const AdminIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M14.2994 9.15C14.3328 8.88333 14.3578 8.61667 14.3578 8.33333C14.3578 8.05 14.3328 7.78333 14.2994 7.51667L16.0578 6.14167C16.2161 6.01667 16.2578 5.79167 16.1578 5.60833L14.4911 2.725C14.4161 2.59167 14.2744 2.51667 14.1244 2.51667C14.0744 2.51667 14.0244 2.525 13.9828 2.54167L11.9078 3.375C11.4744 3.04167 11.0078 2.76667 10.4994 2.55833L10.1828 0.35C10.1578 0.15 9.98278 0 9.77444 0H6.44111C6.23278 0 6.05778 0.15 6.03278 0.35L5.71611 2.55833C5.20778 2.76667 4.74111 3.05 4.30778 3.375L2.23278 2.54167C2.18278 2.525 2.13278 2.51667 2.08278 2.51667C1.94111 2.51667 1.79944 2.59167 1.72444 2.725L0.0577763 5.60833C-0.050557 5.79167 -0.000556918 6.01667 0.157776 6.14167L1.91611 7.51667C1.88278 7.78333 1.85778 8.05833 1.85778 8.33333C1.85778 8.60833 1.88278 8.88333 1.91611 9.15L0.157776 10.525C-0.000556918 10.65 -0.0422237 10.875 0.0577763 11.0583L1.72444 13.9417C1.79944 14.075 1.94111 14.15 2.09111 14.15C2.14111 14.15 2.19111 14.1417 2.23278 14.125L4.30778 13.2917C4.74111 13.625 5.20778 13.9 5.71611 14.1083L6.03278 16.3167C6.05778 16.5167 6.23278 16.6667 6.44111 16.6667H9.77444C9.98278 16.6667 10.1578 16.5167 10.1828 16.3167L10.4994 14.1083C11.0078 13.9 11.4744 13.6167 11.9078 13.2917L13.9828 14.125C14.0328 14.1417 14.0828 14.15 14.1328 14.15C14.2744 14.15 14.4161 14.075 14.4911 13.9417L16.1578 11.0583C16.2578 10.875 16.2161 10.65 16.0578 10.525L14.2994 9.15ZM12.6494 7.725C12.6828 7.98333 12.6911 8.15833 12.6911 8.33333C12.6911 8.50833 12.6744 8.69167 12.6494 8.94167L12.5328 9.88333L13.2744 10.4667L14.1744 11.1667L13.5911 12.175L12.5328 11.75L11.6661 11.4L10.9161 11.9667C10.5578 12.2333 10.2161 12.4333 9.87444 12.575L8.99111 12.9333L8.85778 13.875L8.69111 15H7.52444L7.36611 13.875L7.23278 12.9333L6.34944 12.575C5.99111 12.425 5.65778 12.2333 5.32444 11.9833L4.56611 11.4L3.68278 11.7583L2.62444 12.1833L2.04111 11.175L2.94111 10.475L3.68278 9.89167L3.56611 8.95C3.54111 8.69167 3.52444 8.5 3.52444 8.33333C3.52444 8.16667 3.54111 7.975 3.56611 7.725L3.68278 6.78333L2.94111 6.2L2.04111 5.5L2.62444 4.49167L3.68278 4.91667L4.54944 5.26667L5.29944 4.7C5.65778 4.43333 5.99944 4.23333 6.34111 4.09167L7.22444 3.73333L7.35778 2.79167L7.52444 1.66667H8.68278L8.84111 2.79167L8.97444 3.73333L9.85778 4.09167C10.2161 4.24167 10.5494 4.43333 10.8828 4.68333L11.6411 5.26667L12.5244 4.90833L13.5828 4.48333L14.1661 5.49167L13.2744 6.2L12.5328 6.78333L12.6494 7.725ZM8.10778 5C6.26611 5 4.77444 6.49167 4.77444 8.33333C4.77444 10.175 6.26611 11.6667 8.10778 11.6667C9.94944 11.6667 11.4411 10.175 11.4411 8.33333C11.4411 6.49167 9.94944 5 8.10778 5ZM8.10778 10C7.19111 10 6.44111 9.25 6.44111 8.33333C6.44111 7.41667 7.19111 6.66667 8.10778 6.66667C9.02444 6.66667 9.77444 7.41667 9.77444 8.33333C9.77444 9.25 9.02444 10 8.10778 10Z"
    fill="#2D2D2D"
  />
);

// ActionPlanIcon - same SVG used for web/iPad so mobile matches
const TruckIcon: React.FC<{ color?: string }> = ({ color = '#3A3A3A' }) => (
  <Path
    d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zm-.5 1.5l1.96 2.5H17V9.5h2.5zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2.22-3c-.55-.61-1.33-1-2.22-1s-1.67.39-2.22 1H3V6h12v9H8.22zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"
    fill="#3A3A3A"
  />
);

interface HarborIconProps {
  color?: string;
}

// Harbor anchor icon — designed in 24x24 space to match other sidebar icons
const HarborIcon: React.FC<HarborIconProps> = ({ color = '#3A3A3A' }) => (
  <G
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Ring */}
    <Circle cx="12" cy="5" r="2.1" />

    {/* Shank */}
    <Line x1="12" y1="7.1" x2="12" y2="19" />

    {/* Stock */}
    <Line x1="8" y1="10" x2="16" y2="10" />

    {/* Curved Flukes */}
    <Path d="M6 14A6 6 0 0 0 18 14" />
  </G>
);

const ActionPlanIcon: React.FC<{ color?: string }> = ({ color = '#2D2D2D' }) => (
  <Path d="M19.1667 6.66667C19.1667 7.58333 18.4167 8.33333 17.5 8.33333C17.35 8.33333 17.2084 8.31667 17.075 8.275L14.1084 11.2333C14.15 11.3667 14.1667 11.5167 14.1667 11.6667C14.1667 12.5833 13.4167 13.3333 12.5 13.3333C11.5834 13.3333 10.8334 12.5833 10.8334 11.6667C10.8334 11.5167 10.85 11.3667 10.8917 11.2333L8.76671 9.10833C8.63337 9.15 8.48337 9.16667 8.33337 9.16667C8.18337 9.16667 8.03337 9.15 7.90004 9.10833L4.10837 12.9083C4.15004 13.0417 4.16671 13.1833 4.16671 13.3333C4.16671 14.25 3.41671 15 2.50004 15C1.58337 15 0.833374 14.25 0.833374 13.3333C0.833374 12.4167 1.58337 11.6667 2.50004 11.6667C2.65004 11.6667 2.79171 11.6833 2.92504 11.725L6.72504 7.93333C6.68337 7.8 6.66671 7.65 6.66671 7.5C6.66671 6.58333 7.41671 5.83333 8.33337 5.83333C9.25004 5.83333 10 6.58333 10 7.5C10 7.65 9.98337 7.8 9.94171 7.93333L12.0667 10.0583C12.2 10.0167 12.35 10 12.5 10C12.65 10 12.8 10.0167 12.9334 10.0583L15.8917 7.09167C15.85 6.95833 15.8334 6.81667 15.8334 6.66667C15.8334 5.75 16.5834 5 17.5 5C18.4167 5 19.1667 5.75 19.1667 6.66667Z" fill="#2D2D2D" />
);

const renderIconChild = (child: any, iconColor: string): any => {
  if (!child) return child;
  if (React.isValidElement(child)) {
    if (child.type === Path || child.type === Circle || child.type === Line || child.type === G) {
      const props: any = { ...child.props };
      if (props.fill && props.fill !== 'none') {
        props.fill = iconColor;
      }
      if (props.stroke && props.stroke !== 'none') {
        props.stroke = iconColor;
      }
      if (props.children) {
        props.children = React.Children.map(props.children, c => renderIconChild(c, iconColor));
      }
      return React.cloneElement(child, props);
    }
    const elementProps = (child.props || {}) as Record<string, any>;
    return React.cloneElement(child, {
      ...elementProps,
      color: iconColor,
      children: elementProps.children
        ? React.Children.map(elementProps.children, c => renderIconChild(c, iconColor))
        : undefined,
    });
  }
  return child;
};

const DynamicIcon = ({ 
  isActive, 
  isHovered, 
  isDisabled = false,
  isDark = false,
  children 
}: { 
  isActive: boolean; 
  isHovered: boolean; 
  isDisabled?: boolean;
  isDark?: boolean;
  children: React.ReactNode; 
}) => {
  const iconColor = isDisabled 
    ? (isDark ? '#555555' : '#BDBDBD') 
    : (isActive || isHovered) 
      ? (isDark ? '#3D8BFF' : '#005CBC') 
      : (isDark ? '#B0B0B0' : '#3A3A3A');

  return (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      {React.Children.map(children, (child: any) => renderIconChild(child, iconColor))}
    </Svg>
  );
};

/*
  return null;
};
  isActive, 
  isHovered, 
  children 
}: { 
  isActive: boolean; 
  isHovered: boolean; 
  children: React.ReactNode; 
}) => {
  return (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      {React.Children.map(children, (child: any) => {
        if (React.isValidElement(child) && child.type === Path) {
          const originalFill = (child.props as any).fill || "#3A3A3A";
          return React.cloneElement(child as React.ReactElement<any>, {
            ...(child.props as any),
            fill: (isActive || isHovered) ? "#005CBC" : originalFill
          });
        }
        return child;
      })}
    </Svg>
  );
};

*/

const MobileSidepanel: React.FC<MobileSidepanelProps> = ({
  isVisible,
  onClose,
  activeScreen,
  onNavigate,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const styles = useMemo(() => createMobileSidepanelStyles(isDark), [isDark]);

  const slideAnim = React.useRef(new Animated.Value(isVisible ? 0 : -100)).current; // Start based on initial visibility

  // Mirrors DLOHeader's own breakpoint: header is 48+54=102px in the
  // 768-1024 landscape/tablet width range, 64+54=118px otherwise. This was
  // hardcoded to 118 here, leaving a gap under the header in that range.
  const [headerHeight, setHeaderHeight] = React.useState(() => {
    const { width } = Dimensions.get('window');
    return width >= 768 && width <= 1024 ? 102 : 118;
  });
  React.useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setHeaderHeight(window.width >= 768 && window.width <= 1024 ? 102 : 118);
    });
    return () => subscription.remove();
  }, []);

  React.useEffect(() => {
    console.log('MobileSidepanel visibility changed:', isVisible);
    if (isVisible) {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: -100,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [isVisible, slideAnim]);

  // Map activeScreen to sidebar item ID
  const getActiveItemFromScreen = (screen: string): string => {
    switch (screen) {
      case 'Home': return 'home';
      case 'Observations': return 'observations';
      case 'Shift Handover': return 'shift-handover';
      case 'Leadership Walk': return 'leadership-walk';
      case 'SPARC Walk': return 'sparc-walk';
      case 'NHO': return 'nho';
      case 'Racking Inspection': return 'racking-inspection';
      case 'Quality Audit': return 'quality-audit';
      case 'Site Evaluation': return 'site-evaluation';
      case 'Analytics': return 'analytics';
      case 'Ticketing Portal - Contact Us': return 'ticketing-portal';
      case 'DLODashboard':
      case 'DLO': return 'labor-optimization';
      case 'Admin Dashboard': return 'admin-dashboard';
      case 'Contact us/Feedback': return 'contact-feedback';
      case 'Action Plan': return 'action-plan';
      
      case 'Perfect Truck': return 'perfect-truck';
      case 'Harbor': return 'harbor';
      default: return 'home';
    }
  };

  const activeItem = getActiveItemFromScreen(activeScreen);


  // Get current user's role name
  const userRoleName = useSelector((state: RootState) => state.appData.userInfo?.roleName);

  const is3plLocation = useSelector(
    (state: RootState) => !!state.appData.selectedLocation?.is3pl
  );

  // When switching to a 3PL location, keep users on allowed screens only.
  React.useEffect(() => {
    if (!is3plLocation) {
      return;
    }

    const allowedItemsFor3pl = new Set(['home', 'perfect-truck', 'ticketing-portal']);
    if (!allowedItemsFor3pl.has(activeItem)) {
      onNavigate('Home');
    }
  }, [is3plLocation, activeItem, onNavigate]);

  // Get module access from Redux
  const moduleAccess = useSelector((state: RootState) => state.appData.moduleAccess);

  // Helper function to check if a module has access
  const hasModuleAccess = (moduleName: string): boolean => {
    if (!moduleAccess || !Array.isArray(moduleAccess)) {
      return false;
    }
    const module = moduleAccess.find(m => m.moduleName === moduleName);
    return module ? module.hasAccess : false;
  };

  const shouldShowAnalyticsHub = hasModuleAccess('Analytics');

  // Create a computed map of which sidebar items are disabled
  const disabledItems = useMemo(() => {
    const disabled: { [key: string]: boolean } = {};

    // Each sidebar item is gated by its own dedicated module name from the API
    // Each sidebar item is gated by its own dedicated module name from the API
    disabled['observations'] = !hasModuleAccess('Observations');
    disabled['shift-handover'] = !hasModuleAccess('Shift Handover');
    disabled['employee-update'] = !hasModuleAccess('Employee Update');
    disabled['employee-update'] = !hasModuleAccess('Employee Update');
    disabled['leadership-walk'] = !hasModuleAccess('Leadership Walk');
    disabled['sparc-walk'] = !hasModuleAccess('SPARC Walk');
    disabled['racking-inspection'] = !hasModuleAccess('Racking Inspection');
    disabled['admin-dashboard'] = !hasModuleAccess('Config');
    disabled['quality-audit'] = !hasModuleAccess('Quality Audit');
    disabled['site-evaluation'] = !hasModuleAccess('Site Evaluation');
    disabled['labor-optimization'] = !hasModuleAccess('DLO');
    disabled['nho'] = !hasModuleAccess('NHO');
    disabled['stand-up'] = !hasModuleAccess('Stand Up');
    disabled['stand-up'] = !hasModuleAccess('Stand Up');
    disabled['perfect-truck'] = !hasModuleAccess('Perfect Truck');
    disabled['harbor'] = !hasModuleAccess('Perfect Truck')|| !hasModuleAccess('Delivery RPM');
    disabled['analytics'] = !hasModuleAccess('Analytics');
    disabled['ticketing-portal'] = false;
    disabled['action-plan'] = !hasModuleAccess('Action Plan');

    return disabled;
  }, [moduleAccess]);

  const NavigationItem = ({
    id,
    iconChildren,
    label,
    disabled = false,
  }: {
    id: string;
    iconChildren: React.ReactNode;
    label: string;
    disabled?: boolean;
  }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const isActive = activeItem === id;
    
    const handlePress = () => {
      if (disabled) return; // Don't navigate if disabled
      
      switch (id) {
        case 'home': onNavigate('Home'); break;
        case 'observations': onNavigate('Observations'); break;
        case 'shift-handover': onNavigate('Shift Handover'); break;
        case 'admin-dashboard': onNavigate('Admin Dashboard'); break;
        case 'leadership-walk': onNavigate('Leadership Walk'); break;
        case 'sparc-walk': onNavigate('SPARC Walk'); break;
        case 'nho': onNavigate('NHO'); break;
        case 'racking-inspection': onNavigate('Racking Inspection'); break;
        case 'quality-audit': onNavigate('Quality Audit'); break;
        case 'site-evaluation': onNavigate('Site Evaluation'); break;
        case 'analytics': onNavigate('Analytics'); break;
        case 'labor-optimization': onNavigate('DLODashboard'); break;
        case 'action-plan': onNavigate('Action Plan'); break;
        case 'perfect-truck': onNavigate('Perfect Truck'); break;
        case 'harbor': onNavigate('Harbor'); break;
        case 'ticketing-portal': onNavigate('Ticketing Portal'); break;
        case 'contact-feedback':
          // Open external PowerApps URL
          if (Platform.OS === 'web') {
            window.open('https://apps.powerapps.com/play/e/default-42cc3295-cd0e-449c-b98e-5ce5b560c1d3/a/e2fdf8bc-ab8c-44c2-8cd3-d1b22f94de19?tenantId=42cc3295-cd0e-449c-b98e-5ce5b560c1d3#', '_blank');
          } else {
            Linking.openURL('https://apps.powerapps.com/play/e/default-42cc3295-cd0e-449c-b98e-5ce5b560c1d3/a/e2fdf8bc-ab8c-44c2-8cd3-d1b22f94de19?tenantId=42cc3295-cd0e-449c-b98e-5ce5b560c1d3#');
          }
          break;
        default: onNavigate(id); break;
      }
      onClose(); // Close panel after navigation
    };
    
    return (
      <Pressable
        disabled={disabled}
        style={({ pressed }) => [
          styles.navItem,
          isActive && !disabled && styles.activeNavItem,
          (isHovered || pressed) && !disabled && styles.hoveredNavItem,
          disabled && styles.disabledNavItem
        ]}
        onPress={handlePress}
        onHoverIn={() => !disabled && setIsHovered(true)}
        onHoverOut={() => !disabled && setIsHovered(false)}
      >
        <View style={styles.navItemContent}>
          <DynamicIcon isActive={isActive} isHovered={isHovered && !disabled} isDisabled={disabled} isDark={isDark}>
            {iconChildren}
          </DynamicIcon>
          <Text style={[
            styles.navItemLabel,
            (isActive || isHovered) && !disabled && { color: isDark ? '#3D8BFF' : '#005CBC' },
            disabled && styles.disabledNavItemLabel
          ]}>
            {label}
          </Text>
        </View>
      </Pressable>
    );
  };

  // Always render when visible to allow animation
  return (
    <Animated.View 
      style={[
        styles.container,
        { top: headerHeight },
        {
          opacity: isVisible ? 1 : 0,
          transform: [
            {
              translateX: slideAnim.interpolate({
                inputRange: [-100, 0],
                outputRange: [-500, 0], // Slide from left
              })
            }
          ]
        }
      ]}
      pointerEvents={isVisible ? 'auto' : 'none'}
    >
      <ScrollView 
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Close Button */}
        <Pressable style={styles.closeButton} onPress={onClose}>
          <View style={styles.closeButtonContent}>
            {/* <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <CloseIcon />
            </Svg>
            <Text style={styles.closeButtonText}>Close</Text> */}
          </View>
        </Pressable>

        {/* Navigation Items */}
        <View style={styles.menuList}>
          <NavigationItem id="home" iconChildren={<HomeIcon />} label="Dashboard" disabled={disabledItems['home']} />

          {(
            <>
              <NavigationItem id="observations" iconChildren={<VisibilityIcon />} label="Observations" disabled={disabledItems['observations']} />
              <NavigationItem id="shift-handover" iconChildren={<SwapIcon />} label="Shift Handover" disabled={disabledItems['shift-handover']} />
              <NavigationItem id="leadership-walk" iconChildren={<WalkIcon />} label="Leadership Walk" disabled={disabledItems['leadership-walk']} />
              <NavigationItem id="sparc-walk" iconChildren={<ExploreIcon />} label="SPARC Walk" disabled={disabledItems['sparc-walk']} />
              <NavigationItem id="nho" iconChildren={<PersonAddIcon />} label="NHO" disabled={disabledItems['nho']} />
              <NavigationItem id="racking-inspection" iconChildren={<InventoryIcon />} label="Racking Inspection" disabled={disabledItems['racking-inspection']} />
              <NavigationItem id="quality-audit" iconChildren={<PasteIcon />} label="Quality Audit" disabled={disabledItems['quality-audit']} />
              <NavigationItem id="site-evaluation" iconChildren={<VerificationIcon />} label="Site Evaluation" disabled={disabledItems['site-evaluation']} />
              {shouldShowAnalyticsHub && (
                <NavigationItem id="analytics" iconChildren={<AnalyticsIcon />} label="Analytics Hub" disabled={disabledItems['analytics']} />
              )}

              {/* Labor Optimization - Only show on web */}
              {Platform.OS !== 'ios' && (
                <NavigationItem id="labor-optimization" iconChildren={<TrendingIcon />} label="Labor Optimization" disabled={disabledItems['labor-optimization']} />
              )}
              <NavigationItem id="action-plan" iconChildren={<ActionPlanIcon />} label="Action Plan" disabled={disabledItems['action-plan']} />
              <NavigationItem id="harbor" iconChildren={<HarborIcon />} label="Harbor" disabled={disabledItems['harbor']} />
              <NavigationItem id="admin-dashboard" iconChildren={<AdminIcon />} label="Configurations" disabled={disabledItems['admin-dashboard']} />
              <NavigationItem id="ticketing-portal" iconChildren={<FolderIcon />} label="Ticketing Portal - Contact Us" disabled={disabledItems['ticketing-portal']} />
            </>
          )}
        </View>
      </ScrollView>
    </Animated.View>
  );
};

const createMobileSidepanelStyles = (isDark: boolean) => StyleSheet.create({
  container: {
    ...(Platform.OS === 'web' ? { position: 'fixed' as any } : { position: 'absolute' }),
    top: 118, // Below header (64px) + subheader (54px)
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isDark ? '#202020' : '#FBFCFE',
    borderRightWidth: 1,
    borderRightColor: isDark ? '#363636' : 'transparent',
    zIndex: 999,
    ...Platform.select({
      ios: {
        shadowColor: isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 92, 188, 0.16)',
        shadowOffset: { width: 2, height: 4 },
        shadowOpacity: 1,
        shadowRadius: 24,
      },
      android: {
        elevation: 16,
      },
      web: {
        boxShadow: isDark ? '2px 4px 24px rgba(0, 0, 0, 0.4)' : '2px 4px 24px rgba(0, 92, 188, 0.16)',
      } as any,
    }),
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
  closeButton: {
    height: 56,
    paddingHorizontal: 24,
    justifyContent: 'center',
    backgroundColor: isDark ? '#202020' : '#FBFCFE',
    borderBottomWidth: 1,
    borderBottomColor: isDark ? '#363636' : '#E0E0E0',
  },
  closeButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  closeButtonText: {
    flex: 1,
    color: isDark ? '#B0B0B0' : '#616161',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'Inter',
    fontSize: 14.5,
    fontWeight: '400',
    lineHeight: 20,
  },
  menuList: {
    gap: 4,
    paddingTop: 8,
  },
  navItem: {
    height: 50,
    paddingHorizontal: 24,
    justifyContent: 'center',
    backgroundColor: isDark ? '#202020' : '#FBFCFE',
    width: '100%',
  },
  activeNavItem: {
    borderLeftWidth: 5,
    borderLeftColor: isDark ? '#3D8BFF' : '#005CBC',
    backgroundColor: isDark ? 'rgba(61, 139, 255, 0.12)' : '#FBFCFE',
  },
  hoveredNavItem: {
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#EFF5FC',
  },
  navItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  navItemLabel: {
    flex: 1,
    color: isDark ? '#B0B0B0' : '#616161',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'Inter',
    fontSize: 14.5,
    fontWeight: '400',
    lineHeight: 20,
  },
  disabledNavItem: {
    opacity: 0.4,
    backgroundColor: isDark ? '#202020' : '#F5F5F5',
  },
  disabledNavItemLabel: {
    color: isDark ? '#555555' : '#999999',
  },
});

export default MobileSidepanel;
